import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { CheckCircle2, XCircle, Clock, FileText } from "lucide-react";

/**
 * Testimonial admin — full lifecycle:
 *   pending_review → (edit published_text) → approve → publish
 *                                        → withdraw (removes from public)
 * Also shows the consent snapshot (immutable) + audit trail.
 */
const STATUS_STYLE = {
  pending_review: "bg-amber-50 text-amber-900 border-amber-200",
  approved:       "bg-green-50 text-green-800 border-green-200",
  withdrawn:      "bg-rose-50 text-rose-700 border-rose-200",
};

export function TestimonialsAdmin() {
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  const [audit, setAudit] = useState(null);
  const [edit, setEdit] = useState({ published_text: "", service_category: "", clinic_location: "", display_name: "" });
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/testimonials");
      setRows(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => rows.filter(r => statusFilter === "all" || r.status === statusFilter), [rows, statusFilter]);

  const select = async (r) => {
    setSel(r);
    setEdit({
      published_text: r.published_text || r.original_text || "",
      service_category: r.service_category || "",
      clinic_location: r.clinic_location || "",
      display_name: r.display_name || "",
    });
    try {
      const { data } = await api.get(`/admin/testimonials/${r.id}/audit`);
      setAudit(data);
    } catch { setAudit(null); }
  };

  const save = async () => {
    try {
      await api.post(`/admin/testimonials/${sel.id}`, edit);
      toast.success("Saved");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const approve = async () => {
    try {
      await api.post(`/admin/testimonials/${sel.id}/approve`);
      toast.success("Approved & published");
      await load();
      setSel(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
  };
  const withdraw = async () => {
    if (!window.confirm("Withdraw this testimonial? It will no longer be publicly visible.")) return;
    try {
      await api.post(`/admin/testimonials/${sel.id}/withdraw`);
      toast.success("Withdrawn");
      await load();
      setSel(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Withdraw failed"); }
  };

  return (
    <div data-testid="testimonials-admin" className="grid lg:grid-cols-[340px_1fr] gap-6">
      <aside>
        <div className="flex gap-2 mb-3 flex-wrap">
          {["all","pending_review","approved","withdrawn"].map(s => (
            <button key={s} data-testid={`test-status-filter-${s}`} onClick={() => setStatusFilter(s)}
              className={`text-[11px] px-2.5 py-1 rounded-full border ${statusFilter === s ? "bg-stone-900 text-white border-stone-900" : "bg-white border-stone-200"}`}>
              {s.replace("_", " ")}
            </button>
          ))}
        </div>
        <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">{filtered.length} testimonials</div>
        <div className="space-y-2">
          {filtered.map(r => (
            <button key={r.id} onClick={() => select(r)}
              data-testid={`test-row-${r.id}`}
              className={`w-full text-left vv-card p-3 hover:border-[color:var(--vv-gold)] ${sel?.id === r.id ? "border-[color:var(--vv-gold-deep)]" : ""}`}>
              <div className="text-sm font-medium">{r.display_name || "Anonymous"}</div>
              <div className="text-xs text-stone-500 truncate">{r.service_category} · {r.clinic_location || "—"}</div>
              <div className="mt-1 flex items-center gap-2">
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${STATUS_STYLE[r.status] || "bg-stone-100"}`}>{r.status?.replace("_"," ")}</span>
                {r.rating ? <span className="text-[10px] text-amber-700">{"★".repeat(r.rating)}</span> : null}
              </div>
            </button>
          ))}
          {rows.length === 0 && <div className="vv-card p-4 text-sm text-stone-500 text-center"><FileText className="mx-auto mb-2 opacity-50" size={20} />No testimonials yet.</div>}
        </div>
      </aside>

      <section>
        {sel ? (
          <div className="space-y-4">
            <div className="vv-card p-5">
              <div className="flex justify-between items-start gap-4 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-widest text-stone-500">{sel.status?.replace("_"," ")}</div>
                  <h2 className="font-display text-xl">{sel.display_name || "Anonymous"}</h2>
                </div>
                <div className="flex gap-2">
                  <button onClick={save} data-testid="test-save" className="text-sm bg-white border border-stone-200 rounded-full px-3 py-1.5">Save edits</button>
                  {sel.status === "pending_review" && (
                    <button onClick={approve} data-testid="test-approve" className="text-sm text-white bg-[color:var(--vv-gold-deep)] rounded-full px-4 py-1.5">Approve & publish</button>
                  )}
                  {sel.status === "approved" && (
                    <button onClick={withdraw} data-testid="test-withdraw" className="text-sm text-rose-700 bg-white border border-rose-200 rounded-full px-3 py-1.5">Withdraw</button>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="text-xs font-medium">Display name</label>
                  <input value={edit.display_name} onChange={e => setEdit({...edit, display_name: e.target.value})}
                         className="w-full mt-1 border border-stone-200 rounded px-2 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-medium">Service category</label>
                  <input value={edit.service_category} onChange={e => setEdit({...edit, service_category: e.target.value})}
                         className="w-full mt-1 border border-stone-200 rounded px-2 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-medium">Clinic location</label>
                  <input value={edit.clinic_location} onChange={e => setEdit({...edit, clinic_location: e.target.value})}
                         className="w-full mt-1 border border-stone-200 rounded px-2 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-medium">Date received</label>
                  <div className="mt-1 text-sm text-stone-500">{sel.date_received?.slice(0,10)}</div>
                </div>
              </div>

              <div className="mt-4">
                <label className="text-xs font-medium">Original text (immutable — patient's own words)</label>
                <div className="mt-1 text-sm bg-stone-50 border border-stone-200 rounded p-3 text-stone-700 whitespace-pre-wrap">{sel.original_text}</div>
              </div>
              <div className="mt-3">
                <label className="text-xs font-medium">Published text (this is what appears publicly)</label>
                <textarea value={edit.published_text} onChange={e => setEdit({...edit, published_text: e.target.value})}
                          rows={5} className="w-full mt-1 border border-stone-200 rounded p-2 text-sm" />
              </div>
            </div>

            <div className="vv-card p-5">
              <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Consent (immutable)</div>
              <div className="text-xs space-y-1">
                <div><strong>Method:</strong> {sel.consent_method}</div>
                <div><strong>Granted at:</strong> {sel.consent_granted_at}</div>
                <div className="mt-2"><strong>Consent language:</strong></div>
                <div className="bg-stone-50 border border-stone-200 rounded p-2 whitespace-pre-wrap">{sel.consent_text}</div>
              </div>
            </div>

            {audit?.audit?.length ? (
              <div className="vv-card p-5">
                <div className="text-xs uppercase tracking-widest text-stone-500 mb-2 flex items-center gap-1.5"><Clock size={12} /> Audit history</div>
                <ul className="space-y-2 text-xs">
                  {audit.audit.map((a, i) => (
                    <li key={`${a.at || "no-at"}-${a.action || "no-action"}-${i}`} className="border-l-2 border-stone-200 pl-3">
                      <div className="text-stone-800">{a.action}</div>
                      <div className="text-stone-500">{a.actor} · {a.at}</div>
                      {a.note ? <div className="text-stone-500 italic mt-0.5">{a.note}</div> : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="vv-card p-10 text-center text-stone-500">Select a testimonial to review.</div>
        )}
      </section>
    </div>
  );
}
